/** Strings of the accounting screens cutover, opening balances, journal, ledger, tax documents, missing documents, invoices and expenses: `accLedgerEn` is the source, `accLedgerCs` must have the same keys. */
export const accLedgerEn = {
  accCutover: {
    verdict: {
      never: "Never checked",
      failed: "Check failed",
      running: "Checking…",
      differences_one: "{{count}} difference",
      differences_few: "{{count}} differences",
      differences_other: "{{count}} differences",
      withoutPdf: "{{count}} document(s) without an archived PDF",
      clear: "Hub holds everything Fakturoid does",
    },
    title: "Leaving Fakturoid",
    subtitle:
      "While the migration runs, Fakturoid stays the system of record and Hub keeps a full copy — this page says whether that copy is complete.",
    queueing: "Queueing…",
    importButton: "Import from Fakturoid",
    checkNow: "Check now",
    canSwitchOff: "Can Fakturoid be switched off?",
    issuedDocuments: "Issued documents",
    receivedDocuments: "Received documents",
    withoutArchivedPdf: "Without archived PDF",
    documentsChecked: "Documents checked",
    lastCheck: "Last check:",
    lastImport: "last import:",
    never: "never",
    beforeCancel: "Before the subscription is cancelled",
    beforeCancelHint:
      "A cancelled account will not hand over the documents' PDFs, and the law wants them kept for ten years. While any blocking condition fails, Fakturoid stays.",
    readyToSwitchOff: "Ready to switch off",
    notYet: "Not yet",
    optional: "optional",
    differences: "Differences",
    nothingDiffers: "Nothing — every document lines up.",
    noCheckYet: "No check has run yet, so nothing is known.",
    side: "Side",
    document: "Document",
    kind: "Kind",
    detail: "Detail",
    sides: {
      issued: "issued",
      received: "received",
    },
  },
  accOpening: {
    title: "Opening balances",
    subtitle:
      "The state the company arrived with. Until these are posted, the balance sheet doesn't add up and the receivables ledger says nothing about anything older than the migration.",
    year: "Year",
    posted: "Posted",
    readyToPost: "Ready to post",
    nothingYet: "Nothing yet",
    totals: "Debit {{debit}} · credit {{credit}}",
    totalsDifference: " · difference {{difference}}",
    journalEntryNo: "journal entry no. {{number}}",
    posting: "Posting…",
    postToJournal: "Post to the journal",
    unbalanced:
      "Debit and credit differ by {{difference}}. An entry that doesn't add up can't be posted — the figures need fixing at the source.",
    postedOnce:
      "Posted once, on purpose. A correction from here on is an ordinary journal entry — the journal isn't edited.",
    account: "Account",
    debit: "Debit",
    credit: "Credit",
    total: "Total",
    pasteTitle: "Paste the trial balance",
    pasteHint:
      "One account per line: <code>account;debit;credit</code>. Replaces whatever is here — until it's posted, nothing is set in stone.",
    pastePlaceholder: "311;125000;0\n321;0;48000\n221;310000;0",
    reading: "Reading…",
    readAndSave: "Read and save",
    readFailed: "Couldn't read the opening balances.",
    postFailed: "Couldn't post them.",
  },
  accJournal: {
    seedFailed: "Couldn't create the chart of accounts.",
    reverseFailed: "The reversal failed.",
    title: "Journal {{year}}",
    subtitle:
      "An entry is never edited or deleted. A correction is a reversal plus a new entry — both stay in the journal.",
    periodClosed: "period closed",
    trialBalance: "Trial balance",
    trialBalanceHint:
      "Both sides have to add up — when they don't, there is no point looking at the statements.",
    sidesAgree: "sides agree",
    sidesDisagree: "sides disagree",
    nothingPosted:
      "Nothing is posted for this year. If the chart of accounts is empty too, create it from the standard chart.",
    creating: "Creating…",
    seed: "Create the chart of accounts and templates",
    account: "Account",
    name: "Name",
    debit: "Debit",
    credit: "Credit",
    balance: "Balance",
    total: "Total",
    journal: "Journal",
    entries_one: "{{count}} entry",
    entries_few: "{{count}} entries",
    entries_other: "{{count}} entries",
    nothingPostedYet: "Nothing posted yet.",
    entryNo: "no. {{number}}",
    storno: "reversal",
    reverse: "reverse",
    reversePrompt: "Reason for the reversal:",
  },
  accLedger: {
    title: "Open items",
    asOf: "As of <date/> — only payments received by that day count.",
    asOfLabel: "As of",
    receivables: "Receivables",
    receivablesHint: "What customers owe us",
    payables: "Payables",
    payablesHint: "What we owe suppliers",
    documents_one: "{{count}} document",
    documents_few: "{{count}} documents",
    documents_other: "{{count}} documents",
    nothingOpen: "Nothing open.",
    doc: "Document",
    partner: "Partner",
    dueDate: "Due date",
    total: "Total",
    paid: "Paid",
    outstanding: "Outstanding",
    days_one: "{{count}} day",
    days_few: "{{count}} days",
    days_other: "{{count}} days",
  },
  accTaxDocs: {
    title: "Tax documents",
    subtitle:
      "What the company filed with the tax office and what the office sent, from the MOJE daně portal, with every file it offered.",
    documents: "Documents",
    filed: "filed",
    fromOffice: "from the office",
    noFiles: "no files",
    noDocuments: "No documents imported yet.",
    taxAccount: "Tax account",
    taxAccountHint: "Movements on the company's tax account at the office (osobní daňový účet).",
    noMovements: "No movements imported yet.",
    date: "Date",
    tax: "Tax",
    movement: "Movement",
    owed: "Owed",
    paidColumn: "Paid",
  },
  accMissing: {
    title: "Missing documents",
    intro:
      "Bank movements nothing is paired with yet. Send the documents to the <inbox>Inbox</inbox>; a movement that never has a document (a loan, a transfer between own accounts) is put aside with a reason.",
    expensesTitle: "Payments without an expense document",
    expensesHint: "Invoices or receipts to get from these suppliers.",
    incomeTitle: "Income without an invoice",
    incomeHint: "Money that came in and pays no invoice in the books.",
    nothingMissing: "Nothing is missing.",
    card: "card {{amount}} {{currency}}",
    vs: "Var. symbol {{symbol}}",
    putAsideFailed: "Couldn't put it aside.",
    putAsidePrompt: "Why does this movement need no document?",
    noDocumentNeeded: "No document needed",
  },
  accInvoices: {
    title: "Invoices",
    emptyState: "No invoices yet — issue one from a billing report, or run the Fakturoid import.",
    creditNoteIssued: "Credit note issued",
    creditNoteFailed: "Credit note failed",
    issueCreditNote: "Issue credit note",
    dialogTitle: "Issue a credit note",
    dialogDescription:
      "The invoice stays in the books and is cancelled by a mirrored, negative document. Say why — it is what an audit reads later.",
    reason: "Reason",
    reasonPlaceholder: "Wrongly billed hours",
    cancel: "Cancel",
    issuing: "Issuing…",
    backfill: {
      action: "Add report",
      title: "Add a report to this invoice",
      description:
        "The invoice was issued outside Hub billing, so no hours can be paired to it. The report copies its lines as they are; the hours are then paired on Time Entries with “Pair to issued invoice”.",
      from: "Period from",
      to: "Period to",
      submit: "Add report",
      working: "Adding…",
      done: "Report added",
      failed: "The report could not be added",
    },
    openReport: "Billing report",
  },
  accExpenses: {
    title: "Expenses",
    emptyState:
      "No expenses yet — they arrive from Netlion Scanner, from the Fakturoid import, or by hand.",
  },
};

export const accLedgerCs: typeof accLedgerEn = {
  accCutover: {
    verdict: {
      never: "Nikdy nezkontrolováno",
      failed: "Kontrola selhala",
      running: "Kontroluje se…",
      differences_one: "{{count}} rozdíl",
      differences_few: "{{count}} rozdíly",
      differences_other: "{{count}} rozdílů",
      withoutPdf: "{{count}} dokl. bez archivovaného PDF",
      clear: "Hub obsahuje vše, co Fakturoid",
    },
    title: "Odchod z Fakturoidu",
    subtitle:
      "Po dobu migrace zůstává hlavní evidencí Fakturoid a Hub si drží úplnou kopii — tato stránka říká, zda je kopie kompletní.",
    queueing: "Zařazuje se…",
    importButton: "Importovat z Fakturoidu",
    checkNow: "Zkontrolovat nyní",
    canSwitchOff: "Dá se Fakturoid vypnout?",
    issuedDocuments: "Vydané doklady",
    receivedDocuments: "Přijaté doklady",
    withoutArchivedPdf: "Bez archivovaného PDF",
    documentsChecked: "Zkontrolované doklady",
    lastCheck: "Poslední kontrola:",
    lastImport: "poslední import:",
    never: "nikdy",
    beforeCancel: "Před zrušením předplatného",
    beforeCancelHint:
      "Zrušený účet už PDF dokladů nevydá a zákon je žádá uchovávat deset let. Dokud některá blokující podmínka neplatí, Fakturoid zůstává.",
    readyToSwitchOff: "Připraveno k vypnutí",
    notYet: "Ještě ne",
    optional: "nepovinné",
    differences: "Rozdíly",
    nothingDiffers: "Nic — všechny doklady sedí.",
    noCheckYet: "Kontrola ještě neproběhla, takže nic není známo.",
    side: "Strana",
    document: "Doklad",
    kind: "Druh",
    detail: "Detail",
    sides: {
      issued: "vydané",
      received: "přijaté",
    },
  },
  accOpening: {
    title: "Počáteční stavy",
    subtitle:
      "Stav, se kterým firma přišla. Dokud nejsou zaúčtované, rozvaha nesedí a saldokonto pohledávek neříká nic o ničem starším než migrace.",
    year: "Rok",
    posted: "Zaúčtováno",
    readyToPost: "Připraveno k zaúčtování",
    nothingYet: "Zatím nic",
    totals: "Má dáti {{debit}} · dal {{credit}}",
    totalsDifference: " · rozdíl {{difference}}",
    journalEntryNo: "účetní zápis č. {{number}}",
    posting: "Účtuje se…",
    postToJournal: "Zaúčtovat do deníku",
    unbalanced:
      "Má dáti a dal se liší o {{difference}}. Zápis, který nesedí, nelze zaúčtovat — čísla je třeba opravit u zdroje.",
    postedOnce:
      "Zaúčtováno jednou, záměrně. Oprava je od teď běžný účetní zápis — deník se neupravuje.",
    account: "Účet",
    debit: "Má dáti",
    credit: "Dal",
    total: "Celkem",
    pasteTitle: "Vložte obratovou předvahu",
    pasteHint:
      "Jeden účet na řádek: <code>účet;má dáti;dal</code>. Nahradí vše, co tu je — dokud není zaúčtováno, nic není definitivní.",
    pastePlaceholder: "311;125000;0\n321;0;48000\n221;310000;0",
    reading: "Načítá se…",
    readAndSave: "Načíst a uložit",
    readFailed: "Počáteční stavy se nepodařilo načíst.",
    postFailed: "Zaúčtování se nepodařilo.",
  },
  accJournal: {
    seedFailed: "Účtový rozvrh se nepodařilo vytvořit.",
    reverseFailed: "Storno selhalo.",
    title: "Deník {{year}}",
    subtitle:
      "Zápis se nikdy neupravuje ani nemaže. Oprava je storno plus nový zápis — oba zůstávají v deníku.",
    periodClosed: "období uzavřeno",
    trialBalance: "Obratová předvaha",
    trialBalanceHint: "Obě strany musí sedět — když nesedí, nemá smysl dívat se na výkazy.",
    sidesAgree: "strany sedí",
    sidesDisagree: "strany nesedí",
    nothingPosted:
      "Za tento rok není nic zaúčtováno. Pokud je prázdný i účtový rozvrh, vytvořte ho ze standardního rozvrhu.",
    creating: "Vytváří se…",
    seed: "Vytvořit účtový rozvrh a šablony",
    account: "Účet",
    name: "Název",
    debit: "Má dáti",
    credit: "Dal",
    balance: "Zůstatek",
    total: "Celkem",
    journal: "Deník",
    entries_one: "{{count}} zápis",
    entries_few: "{{count}} zápisy",
    entries_other: "{{count}} zápisů",
    nothingPostedYet: "Zatím nic zaúčtováno.",
    entryNo: "č. {{number}}",
    storno: "storno",
    reverse: "stornovat",
    reversePrompt: "Důvod storna:",
  },
  accLedger: {
    title: "Saldokonto",
    asOf: "Ke dni <date/> — počítají se jen platby přijaté do tohoto dne.",
    asOfLabel: "K datu",
    receivables: "Pohledávky",
    receivablesHint: "Co nám dluží zákazníci",
    payables: "Závazky",
    payablesHint: "Co dlužíme dodavatelům",
    documents_one: "{{count}} doklad",
    documents_few: "{{count}} doklady",
    documents_other: "{{count}} dokladů",
    nothingOpen: "Nic otevřeného.",
    doc: "Doklad",
    partner: "Partner",
    dueDate: "Splatnost",
    total: "Celkem",
    paid: "Uhrazeno",
    outstanding: "Zbývá uhradit",
    days_one: "{{count}} den",
    days_few: "{{count}} dny",
    days_other: "{{count}} dní",
  },
  accTaxDocs: {
    title: "Daňové dokumenty",
    subtitle:
      "Co firma podala finančnímu úřadu a co úřad poslal, z portálu MOJE daně, se všemi soubory, které nabízel.",
    documents: "Dokumenty",
    filed: "podáno",
    fromOffice: "od úřadu",
    noFiles: "žádné soubory",
    noDocuments: "Zatím nebyly importovány žádné dokumenty.",
    taxAccount: "Daňový účet",
    taxAccountHint: "Pohyby na osobním daňovém účtu firmy u finančního úřadu.",
    noMovements: "Zatím nebyly importovány žádné pohyby.",
    date: "Datum",
    tax: "Daň",
    movement: "Pohyb",
    owed: "Předpis",
    paidColumn: "Uhrazeno",
  },
  accMissing: {
    title: "Chybějící doklady",
    intro:
      "Bankovní pohyby, ke kterým zatím nic není spárováno. Pošlete doklady do <inbox>Schránky</inbox>; pohyb, který doklad mít nikdy nebude (půjčka, převod mezi vlastními účty), se odloží s uvedením důvodu.",
    expensesTitle: "Platby bez dokladu o nákladu",
    expensesHint: "Faktury nebo účtenky, které je třeba získat od těchto dodavatelů.",
    incomeTitle: "Příjmy bez faktury",
    incomeHint: "Peníze, které přišly a neuhrazují žádnou fakturu v účetnictví.",
    nothingMissing: "Nic nechybí.",
    card: "karta {{amount}} {{currency}}",
    vs: "VS {{symbol}}",
    putAsideFailed: "Odložení se nepodařilo.",
    putAsidePrompt: "Proč tento pohyb nepotřebuje doklad?",
    noDocumentNeeded: "Doklad není potřeba",
  },
  accInvoices: {
    title: "Faktury",
    emptyState:
      "Zatím žádné faktury — vystavte jednu z vyúčtování nebo spusťte import z Fakturoidu.",
    creditNoteIssued: "Dobropis vystaven",
    creditNoteFailed: "Dobropis se nepodařilo vystavit",
    issueCreditNote: "Vystavit dobropis",
    dialogTitle: "Vystavit dobropis",
    dialogDescription:
      "Faktura zůstává v účetnictví a ruší se zrcadlovým, záporným dokladem. Uveďte proč — právě to bude číst audit.",
    reason: "Důvod",
    reasonPlaceholder: "Chybně vyfakturované hodiny",
    cancel: "Zrušit",
    issuing: "Vystavuje se…",
    backfill: {
      action: "Doplnit výkaz",
      title: "Doplnit k faktuře výkaz",
      description:
        "Faktura vznikla mimo výkazy v Hubu, proto k ní nejde napárovat hodiny. Výkaz převezme její položky beze změny, hodiny pak spárujete ve Výkazech času tlačítkem „Spárovat s vystavenou fakturou“.",
      from: "Období od",
      to: "Období do",
      submit: "Doplnit výkaz",
      working: "Doplňuje se…",
      done: "Výkaz doplněn",
      failed: "Výkaz se nepodařilo doplnit",
    },
    openReport: "Výkaz",
  },
  accExpenses: {
    title: "Výdaje",
    emptyState:
      "Zatím žádné výdaje — přicházejí z Netlion Scanneru, z importu z Fakturoidu nebo ručně.",
  },
};
